import type { View } from '@/modules/Editor/views/view'

import type { PolygonShape, Pt } from './geometry'
import { ringsOf } from './geometry'
import type { MaskCrop } from './maskCrop'
import type { MaskSlicePlan } from './planMaskSlice'
import type { PolygonSlicePlan } from './planPolygonSlice'
import type { SlicePreview } from './SliceController'

const STROKE_COLOR = 'rgba(255, 255, 255, 0.95)'
const ERROR_COLOR = 'rgba(255, 90, 79, 0.95)'
const PIECE_FILL = 'rgba(90, 169, 255, 0.45)'
const PIECE_RGBA = [90, 169, 255, 140]
const KEEP_OUTLINE = 'rgba(255, 255, 255, 0.9)'

const tracePath = (
  ctx: CanvasRenderingContext2D,
  view: View,
  points: Pt[],
  close: boolean,
): void => {
  points.forEach((p, i) => {
    const c = view.camera.imageViewToCanvasView(p)
    if (i === 0) ctx.moveTo(c.x, c.y)
    else ctx.lineTo(c.x, c.y)
  })
  if (close) {
    ctx.closePath()
  }
}

const traceShape = (ctx: CanvasRenderingContext2D, view: View, shape: PolygonShape): void => {
  for (const ring of ringsOf(shape)) {
    tracePath(ctx, view, ring, true)
  }
}

const drawStroke = <P>(
  ctx: CanvasRenderingContext2D,
  view: View,
  preview: SlicePreview<P>,
): void => {
  ctx.beginPath()
  tracePath(ctx, view, preview.points, preview.closed)
  ctx.lineWidth = 2
  ctx.strokeStyle = preview.error ? ERROR_COLOR : STROKE_COLOR
  ctx.setLineDash(preview.error ? [7, 5] : [])
  ctx.stroke()
  ctx.setLineDash([])
}

export const drawPolygonPreview = (
  view: View,
  preview: SlicePreview<PolygonSlicePlan> | null,
): void => {
  if (!preview) {
    view.annotationsLayer.draw()
    return
  }
  view.annotationsLayer.draw((ctx) => {
    if (preview.plan) {
      ctx.beginPath()
      traceShape(ctx, view, preview.plan.piece)
      ctx.fillStyle = PIECE_FILL
      ctx.fill('evenodd')
      ctx.beginPath()
      traceShape(ctx, view, preview.plan.keep)
      ctx.strokeStyle = KEEP_OUTLINE
      ctx.lineWidth = 1.5
      ctx.setLineDash([5, 3])
      ctx.stroke()
      ctx.setLineDash([])
    }
    drawStroke(ctx, view, preview)
  })
}

const buildPieceImage = (crop: MaskCrop, piece: Uint8Array): HTMLCanvasElement | null => {
  const canvas = document.createElement('canvas')
  canvas.width = crop.width
  canvas.height = crop.height
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return null
  }
  const image = ctx.createImageData(crop.width, crop.height)
  for (let i = 0; i < piece.length; i++) {
    if (piece[i]) {
      image.data.set(PIECE_RGBA, i * 4)
    }
  }
  ctx.putImageData(image, 0, 0)
  return canvas
}

// Camera-only redraws reuse the overlay; it is rebuilt only for a new piece buffer.
const overlays = new WeakMap<Uint8Array, HTMLCanvasElement | null>()

const pieceImage = (crop: MaskCrop, piece: Uint8Array): HTMLCanvasElement | null => {
  if (overlays.has(piece)) {
    return overlays.get(piece) ?? null
  }
  const image = buildPieceImage(crop, piece)
  overlays.set(piece, image)
  return image
}

export const drawMaskPreview = (
  view: View,
  crop: MaskCrop,
  preview: SlicePreview<MaskSlicePlan> | null,
): void => {
  if (!preview) {
    view.annotationsLayer.draw()
    return
  }
  const overlay = preview.plan ? pieceImage(crop, preview.plan.piece) : null
  view.annotationsLayer.draw((ctx) => {
    if (overlay) {
      const topLeft = view.camera.imageViewToCanvasView({ x: crop.originX, y: crop.originY })
      const bottomRight = view.camera.imageViewToCanvasView({
        x: crop.originX + crop.width,
        y: crop.originY + crop.height,
      })
      ctx.imageSmoothingEnabled = false
      ctx.drawImage(
        overlay,
        topLeft.x,
        topLeft.y,
        bottomRight.x - topLeft.x,
        bottomRight.y - topLeft.y,
      )
    }
    drawStroke(ctx, view, preview)
  })
}
